import { createApi } from '@reduxjs/toolkit/query/react';

import { NOTIFICATION_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface PayerRequestSummaryPreferences {
    payer_request_types?: { code: string; types?: string[] }[];
}

export type PayerNotificationPreferences = Record<string, Record<string, string>>;

const TagType = {
    NOTIFICATION_SETTINGS: 'NOTIFICATION_SETTINGS',
    PAYER_SETTINGS: 'PAYER_SETTINGS',
} as const;

export const notificationApi = createApi({
    baseQuery: getBaseQuery(NOTIFICATION_API_URL),
    reducerPath: 'notificationApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getNotificationPreferences: build.query<
            { email: string[]; in_app: string[]; sms: string[] } & PayerRequestSummaryPreferences,
            void
        >({
            query: () => 'preferences/',
            providesTags: [TagType.NOTIFICATION_SETTINGS],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving notification settings.',
            ),
        }),
        postUpdateNotificationPreferences: build.mutation<
            void,
            {
                email?: string[];
                in_app?: string[];
            } & PayerRequestSummaryPreferences
        >({
            query: (params) => ({
                url: 'preferences/',
                method: 'POST',
                body: {
                    ...params,
                    sms: [], // SMS currently disabled
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred updating notification settings.',
            ),
            invalidatesTags: (_, error) => (!error ? [TagType.NOTIFICATION_SETTINGS] : []),
        }),
        getPayerNotificationPreferences: build.query<PayerNotificationPreferences, void>({
            query: () => 'preferences/payer',
            providesTags: [TagType.PAYER_SETTINGS],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving team notification settings.',
            ),
        }),
        postUpdatePayerNotificationPreferences: build.mutation<void, PayerNotificationPreferences>({
            query: (body) => ({
                url: 'preferences/payer',
                method: 'POST',
                body,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred updating team notifications. Please try again.',
            ),
            invalidatesTags: (_, error) => (!error ? [TagType.PAYER_SETTINGS] : []),
        }),
    }),
});

export const {
    useGetNotificationPreferencesQuery,
    usePostUpdateNotificationPreferencesMutation,
    useGetPayerNotificationPreferencesQuery,
    usePostUpdatePayerNotificationPreferencesMutation,
} = notificationApi;
